import { StepsButtons } from "@/components/button/steps-button";
import TextFieldWithLabel from "@/components/form/hook-form/textfield-with-label";
import { QueryKeys } from "@/constants/query-keys";
import { useGetUser } from "@/hooks/requests/user/use-get-user";
import { useUpdateUser } from "@/hooks/requests/user/use-update-user";
import { useToast } from "@/hooks/ui/use-toast";
import { useStepRouteInfo } from "@/hooks/use-step-route-info";
import { UserDetailsSchema } from "@/schemas/user-schema";
import { setProfileContext } from "@/utils/sentry/tools";
import { zodResolver } from "@hookform/resolvers/zod";
import { IonSkeletonText, useIonViewDidEnter } from "@ionic/react";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { AnimatedOnboardingContainer } from "../../components/animated-onboarding-container";
import { UserDetailSuccessModal } from "../../components/modal/user-detail-success-modal";
import { setMixpanelOnboardingEvent } from "../../utils/mixpanel-onboarding";
import { setSmartlookOnboardingEvent } from "../../utils/smartlook-onboarding";

const OnboardingUserDetailsPage = () => {
  const { info, preloadNextRoute, navigateToNextStep } = useStepRouteInfo();
  const { user, isLoading } = useGetUser();
  const queryClient = useQueryClient();
  const { updateUserAsync, isUpdating } = useUpdateUser();
  const { showError } = useToast();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [submittedFirstName, setSubmittedFirstName] = useState("");
  const firstNameInputRef = useRef<HTMLInputElement>(null);

  useIonViewDidEnter(() => {
    firstNameInputRef.current?.focus();
  });

  const personalInfo = user?.personalInfo;

  const initialValues: z.infer<typeof UserDetailsSchema> = {
    firstName: personalInfo?.firstName ?? "",
    lastName: personalInfo?.lastName ?? "",
  };

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<z.infer<typeof UserDetailsSchema>>({
    defaultValues: initialValues,
    resolver: zodResolver(UserDetailsSchema),
    mode: "onBlur",
    reValidateMode: "onBlur",
  });

  const didHydrateFormRef = useRef(false);
  useEffect(() => {
    if (didHydrateFormRef.current) return;
    // After login, the USER query cache only holds {id, isOnboardingCompleted}
    // until GET /users resolves — guard on personalInfo so we don't hydrate
    // the form with empty strings and then lock further hydration via the ref.
    if (!personalInfo) return;
    reset({
      firstName: personalInfo.firstName ?? "",
      lastName: personalInfo.lastName ?? "",
    });
    didHydrateFormRef.current = true;
  }, [personalInfo, reset]);

  async function handleFormSubmit({ firstName, lastName }: z.infer<typeof UserDetailsSchema>) {
    try {
      const dataBody = UserDetailsSchema.parse({
        firstName,
        lastName,
      });

      await Promise.all([
        preloadNextRoute(),
        updateUserAsync({
          ...dataBody,
          dob: personalInfo?.dob ?? "",
          sex: personalInfo?.sex ?? "",
        }),
      ]);

      queryClient.setQueryData([QueryKeys.USER], (old: object) => {
        return {
          ...old,
          personalInfo: {
            ...personalInfo,
            ...dataBody,
          },
        };
      });
      setProfileContext(QueryKeys.USER, dataBody);

      // Show success modal instead of navigating immediately
      setSmartlookOnboardingEvent({
        type: "submit",
        stepPath: info?.path,
      });
      setMixpanelOnboardingEvent({
        type: "submit",
        stepPath: info?.path,
      });
      setSubmittedFirstName(firstName);
      if (
        user?.personalInfo?.firstName === firstName &&
        user?.personalInfo?.lastName === lastName
      ) {
        navigateToNextStep();
      } else {
        setShowSuccessModal(true);
      }
    } catch (error) {
      if (error instanceof Error) {
        showError(error.message);
      } else {
        showError("An unexpected error occurred");
      }
    }
  }

  const handleModalConfirm = () => {
    setShowSuccessModal(false);
    navigateToNextStep();
  };

  const handleModalEdit = () => {
    setShowSuccessModal(false);
  };

  if (isLoading) {
    return (
      <AnimatedOnboardingContainer title="Hi 👋🏻, I'm Nutricoach, what should I call you?">
        {/* First name field skeleton */}
        <div className="flex flex-col gap-2">
          <IonSkeletonText className="h-5 w-24" animated />
          <IonSkeletonText className="h-12 w-full rounded-lg" animated />
        </div>

        {/* Last name field skeleton */}
        <div className="flex flex-col gap-2">
          <IonSkeletonText className="h-5 w-24" animated />
          <IonSkeletonText className="h-12 w-full rounded-lg" animated />
        </div>
      </AnimatedOnboardingContainer>
    );
  }

  return (
    <AnimatedOnboardingContainer
      title="Hi 👋🏻, I'm Nutricoach, what should I call you?"
      handleSubmit={handleSubmit(handleFormSubmit)}
    >
      <TextFieldWithLabel
        control={control}
        name="firstName"
        label="First name"
        placeholder="Tell me your first name"
        showErrorMessage={false}
        error={errors.firstName?.message}
        inputRef={firstNameInputRef}
      />

      <TextFieldWithLabel
        control={control}
        name="lastName"
        label="Last name"
        placeholder="And your last name"
        showErrorMessage={false}
        error={errors.lastName?.message}
      />

      <StepsButtons type="submit" disabled={isUpdating || !isValid} />

      <UserDetailSuccessModal
        isOpen={showSuccessModal}
        onDismiss={() => setShowSuccessModal(false)}
        firstName={submittedFirstName}
        onConfirm={handleModalConfirm}
        onEdit={handleModalEdit}
      />
    </AnimatedOnboardingContainer>
  );
};

export default OnboardingUserDetailsPage;
